function normalizeIdSegment(value) {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value).trim()
}

export const PARTICIPANT_ID_PREFIXES = Object.freeze({
  user: 'user',
  actor: 'actor',
})

/**
 * Creates the canonical participant id for a human user.
 *
 * @param {string | number | null | undefined} userId
 * @returns {string | null}
 */
export function createUserParticipantId(userId) {
  const normalizedUserId = normalizeIdSegment(userId)

  if (!normalizedUserId) {
    return null
  }

  return `${PARTICIPANT_ID_PREFIXES.user}:${normalizedUserId}`
}

/**
 * Creates the canonical participant id for a non-human actor.
 *
 * @param {string | null | undefined} actorType
 * @param {string | number | null | undefined} stableActorId
 * @returns {string | null}
 */
export function createActorParticipantId(actorType, stableActorId) {
  const normalizedActorType = normalizeIdSegment(actorType)
  const normalizedStableActorId = normalizeIdSegment(stableActorId)

  if (!normalizedActorType || !normalizedStableActorId) {
    return null
  }

  return `${PARTICIPANT_ID_PREFIXES.actor}:${normalizedActorType}:${normalizedStableActorId}`
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isParticipantId(value) {
  if (typeof value !== 'string') {
    return false
  }

  const normalizedValue = value.trim()

  return (
    /^user:[^:\s]+$/i.test(normalizedValue) ||
    /^actor:[^:\s]+:[^:\s]+$/i.test(normalizedValue)
  )
}

/**
 * Resolves the canonical participant id from the provided identity fields.
 *
 * @param {object} [identity={}]
 * @param {string | null | undefined} [identity.participantId]
 * @param {string | number | null | undefined} [identity.userId]
 * @param {string | null | undefined} [identity.actorType]
 * @param {string | number | null | undefined} [identity.actorId]
 * @param {string | number | null | undefined} [identity.stableActorId]
 * @returns {string | null}
 */
export function resolveParticipantId(identity = {}) {
  if (isParticipantId(identity?.participantId)) {
    return identity.participantId.trim()
  }

  const userParticipantId = createUserParticipantId(identity?.userId)

  if (userParticipantId) {
    return userParticipantId
  }

  return createActorParticipantId(
    identity?.actorType,
    identity?.stableActorId ?? identity?.actorId,
  )
}
